import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'selectedLanguage';
  public currentLanguage: string;

  constructor(private translateService: TranslateService) {
    this.currentLanguage = this.getStoredLanguage() || this.getBrowserLanguage();
  }

  /**
   * Initialize translation service with stored or browser language
   */
  initLanguage(): void {
    this.translateService.addLangs(['en', 'es']);
    this.translateService.setDefaultLang('en');
    this.setLanguage(this.currentLanguage);
  }

  /**
   * Change current language
   */
  setLanguage(lang: string): void {
    if (lang !== 'en' && lang !== 'es') {
      lang = 'en';
    }
    this.currentLanguage = lang;
    this.translateService.use(lang);
    this.storeLanguage(lang);
  }

  /**
   * Get current language
   */
  getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Toggle between English and Spanish
   */
  toggleLanguage(): void {
    const newLang = this.currentLanguage === 'en' ? 'es' : 'en';
    this.setLanguage(newLang);
  }

  /**
   * Store language preference in localStorage
   */
  private storeLanguage(lang: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, lang);
    } catch (e) {
      console.error('Error storing language preference:', e);
    }
  }

  /**
   * Get stored language from localStorage
   */
  private getStoredLanguage(): string | null {
    try {
      return localStorage.getItem(this.STORAGE_KEY);
    } catch (e) {
      console.error('Error retrieving language preference:', e);
      return null;
    }
  }

  /**
   * Detect browser language (en or es)
   */
  private getBrowserLanguage(): string {
    const browserLang = navigator.language || (navigator as any).userLanguage;
    // Check if browser language starts with 'es' (español)
    if (browserLang.toLowerCase().startsWith('es')) {
      return 'es';
    }
    // Default to English
    return 'en';
  }
}
